import React from "react";
import { motion } from "framer-motion";
import { APPROACH } from "@/constants/testIds";

const principles = [
    {
        slug: "independence",
        title: "Independence",
        text: [
            "We are owned by no one but the work. There are no investors to appease, no quarterly narratives to construct, no house style to defend. We accept a small number of engagements each year, and we decline the ones we cannot serve well.",
            "Independence is not a marketing position. It is the quiet condition that lets a project take the time it needs to become correct.",
        ],
    },
    {
        slug: "restraint",
        title: "Restraint",
        text: [
            "Most brands say too much. The strongest identities say only what is necessary and hold their line. Restraint is the discipline of removing until only meaning remains — a single letterform, one weight of voice, a measured amount of colour.",
            "We treat restraint as the highest form of confidence. A brand that whispers is heard for far longer than one that shouts.",
        ],
    },
    {
        slug: "craft",
        title: "Craft",
        text: [
            "Craft is what happens between the obvious and the finished — the millimetre of leading, the balance of a serif, the beat of a paragraph. It is the labour that is rarely visible and always felt.",
            "We spend disproportionate time on the details others move past. It is what separates a design from a document, and a system from a template.",
        ],
    },
    {
        slug: "permanence",
        title: "Permanence",
        text: [
            "We design for the decade, not the launch cycle. Trend cycles compress attention; permanence rewards patience. The work we make is intended to look considered ten years from now, not merely new today.",
            "The most enduring identities become invisible. They stop being a design and simply become the thing itself.",
        ],
    },
    {
        slug: "systems",
        title: "Systems",
        text: [
            "A brand is not an artefact — it is an architecture. Logos, colours and typography are only the visible edge of a much larger structure of decisions.",
            "We design systems that hold their shape across teams, languages, formats and years. Frameworks that can be built with by others, not merely admired from a distance.",
        ],
    },
];

const rowVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.9,
            delay: i * 0.05,
            ease: [0.16, 1, 0.3, 1],
        },
    }),
};

export default function Approach() {
    return (
        <section
            id="approach"
            data-testid={APPROACH.section}
            className="py-28 md:py-44 lg:py-56"
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
                {/* Section header */}
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-32">
                    <div className="col-span-12 md:col-span-3">
                        <p className="eyebrow text-[#1B1B1B]/70">
                            § Approach
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                        <motion.h2
                            data-testid={APPROACH.heading}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{
                                duration: 1.1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="font-serif-editorial text-5xl md:text-7xl lg:text-[110px] leading-[0.98] tracking-[-0.02em] text-[#1B1B1B] max-w-5xl"
                        >
                            Designed with{" "}
                            <em className="italic text-[#A8844F]">
                                intention.
                            </em>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 1, delay: 0.3 }}
                            className="mt-12 md:mt-16 font-sans-tight text-base md:text-lg leading-[1.6] text-[#1B1B1B]/80 max-w-2xl font-light"
                        >
                            Five principles guide the way we work. They are
                            not trends we happen to follow, but the standing
                            beliefs we return to before, during and after every
                            project — the reason the work looks the way it
                            does, and behaves the way it behaves, for the
                            years that follow it.
                        </motion.p>
                    </div>
                </div>

                {/* Principles — manifesto rows */}
                <div>
                    {principles.map((p, i) => (
                        <motion.div
                            key={p.slug}
                            data-testid={APPROACH.principleItem(p.slug)}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-8%" }}
                            custom={i}
                            variants={rowVariants}
                            className="grid grid-cols-12 gap-6 md:gap-10 border-t border-[#E6E2D6] py-12 md:py-20"
                        >
                            <div className="col-span-3 md:col-span-2">
                                <span className="font-serif-editorial italic text-[#A8844F] text-5xl md:text-7xl lg:text-[96px] leading-none">
                                    0{i + 1}
                                </span>
                            </div>
                            <div className="col-span-9 md:col-span-4">
                                <h3 className="font-serif-editorial text-3xl md:text-5xl lg:text-6xl text-[#1B1B1B] leading-[1.05]">
                                    {p.title}
                                </h3>
                            </div>
                            <div className="col-span-12 md:col-span-6 md:pl-10 space-y-5">
                                {p.text.map((para, j) => (
                                    <p
                                        key={j}
                                        className="font-sans-tight text-base md:text-lg leading-[1.7] text-[#1B1B1B]/85 max-w-xl font-light"
                                    >
                                        {para}
                                    </p>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                    <div className="hairline" />
                </div>
            </div>
        </section>
    );
}
